import { useState } from 'react';
import { useAppTranslation } from '../hooks/useAppTranslation.js';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function ForgotPasswordModal({ isOpen, onClose }: Props) {
  const { t } = useAppTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const res = await fetch('/api/auth/password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (res.ok) {
        setIsSuccess(true);
      } else {
        setError(data.error || t('auth.forgotPassword.errors.sendFailed', 'E-Mail konnte nicht gesendet werden'));
      }
    } catch {
      setError(t('common.errors.genericRetry', 'Ein Fehler ist aufgetreten. Bitte erneut versuchen.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls =
    'block w-full rounded-lg bg-panel border border-line px-4 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:border-accent-500/60 focus:outline-none focus:ring-1 focus:ring-accent-500/30 transition';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-panel border border-line p-7 shadow-2xl shadow-black/50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-accent-500/15 border border-accent-500/20">
          <svg className="h-5 w-5 text-accent-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
          </svg>
        </div>

        <h3 className="text-lg font-bold text-ink text-center mb-1">
          {t('auth.forgotPassword.title', 'Passwort zurücksetzen')}
        </h3>

        {isSuccess ? (
          <div className="text-center space-y-4">
            <p className="text-sm text-ink-muted mt-2">
              {t(
                'auth.forgotPassword.success',
                'Falls ein Account für {{email}} existiert, erhältst du in Kürze einen Link.',
                { email }
              )}
            </p>
            <button
              onClick={onClose}
              className="w-full rounded-full bg-accent-600 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
            >
              {t('common.actions.close', 'Schließen')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <p className="text-sm text-ink-muted text-center">
              {t(
                'auth.forgotPassword.description',
                'Gib deine E-Mail-Adresse ein und wir schicken dir einen Reset-Link.'
              )}
            </p>

            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1.5">
                {t('common.fields.email', 'E-Mail')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.forgotPassword.emailPlaceholder', 'du@firma.de')}
                className={inputCls}
                required
                autoFocus
              />
            </div>

            {error && (
              <div className="rounded-lg bg-danger-100 dark:bg-danger-500/10 border border-danger-500/20 px-3 py-2 text-sm text-danger-400 text-center">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-full border border-line py-2.5 text-sm font-medium text-ink-muted hover:text-ink hover:border-line-strong transition-all"
              >
                {t('common.actions.cancel', 'Abbrechen')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 rounded-full bg-accent-600 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                {isSubmitting
                  ? t('auth.forgotPassword.actions.sending', 'Senden…')
                  : t('auth.forgotPassword.actions.sendLink', 'Link senden')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
